import { useEffect, useState } from "react";
import { initials } from "@/lib/format";
import { brandLogoUrl } from "@/lib/brand";
import { cn } from "@/lib/utils";

/* remembered per session so long lists never re-request a 404 for the same company */
const failed = new Set();
const loaded = new Set();

const SIZE = {
  xs: "h-[18px] w-[18px] rounded-[4px] text-[8px]",
  sm: "h-6 w-6 rounded-[5px] text-[9.5px]",
  md: "h-8 w-8 rounded-md text-[11px]",
  lg: "h-10 w-10 rounded-lg text-[13px]",
  xl: "h-14 w-14 rounded-xl text-[17px]",
};

/**
 * Company brand mark – logo tile with a monogram fallback (Linear/Attio pattern).
 *   <CompanyMark kind="customer" id={customer_id} name={customer_name} size="md" />
 * `src` overrides the resolved URL. Logos sit on a white tile with a hairline ring so coloured marks stay
 * legible in both themes; the monogram is a quiet neutral tile (colour is semantic in this UI, never decorative).
 */
export const CompanyMark = ({ name, kind = "customer", id, src, size = "md", className, title, testId }) => {
  const url = src || brandLogoUrl(kind, id);
  const [state, setState] = useState(() => (url && loaded.has(url) ? "ok" : url && !failed.has(url) ? "loading" : "none"));
  useEffect(() => { setState(url && loaded.has(url) ? "ok" : url && !failed.has(url) ? "loading" : "none"); }, [url]);
  const ok = state === "ok";
  return (
    <span
      className={cn("relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden ring-1 ring-inset ring-border/70", ok ? "bg-white" : "bg-muted", SIZE[size] || SIZE.md, className)}
      title={title ?? name}
      aria-label={name}
      role="img"
      data-testid={testId || "company-mark"}
      data-mark-state={state}
    >
      {!ok && <span className="font-semibold uppercase leading-none tracking-tight text-foreground/70" aria-hidden>{initials(name)}</span>}
      {url && state !== "none" && (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => { loaded.add(url); setState("ok"); }}
          onError={() => { failed.add(url); setState("none"); }}
          className={cn("absolute inset-0 h-full w-full object-contain p-[12%] transition-opacity duration-200", ok ? "opacity-100" : "opacity-0")}
        />
      )}
    </span>
  );
};
